/**
 * מדיניות מסחרית — the platform-wide commercial defaults. 08 Owner Decision D4, migration 0016.
 *
 * ── WHY THIS SCREEN EXISTS ──────────────────────────────────────────────────────────────────────
 *
 * Because "how long is a trial" was decided by whoever typed the date, one customer at a time, and
 * the platform-wide answer lived nowhere. D4 rules that commercial terms are CONFIGURATION: the
 * Owner changes the default here, without a deploy and with a reason attached.
 *
 * ── AND WHY IT CAN FAIL LOUDLY ──────────────────────────────────────────────────────────────────
 *
 * The server refuses a policy row it cannot parse rather than falling back to a constant, so this
 * screen can legitimately show a failed read where a number used to appear. That is the intended
 * behaviour and not a gap: a default nobody chose, applied silently to every new customer, is worse
 * than a screen that says the setting is unreadable.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ServerRead } from '../components/ServerRead';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { formatInstantHe } from '../api/adminVocabulary';
import { routes } from '../routes';

const TITLE_HE = 'מדיניות מסחרית';

export function PlatformCommercialPolicyScreen(): JSX.Element {
  const ctx = useWorkContext();

  const read = useCallback(() => callOperation('platform.policy.read', {}), []);
  const policyRead = useServerData(read);

  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [sending, setSending] = useState(false);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  function setTrialDays(e: FormEvent): void {
    e.preventDefault();
    const form = new FormData(e.target as HTMLFormElement);
    setSending(true);
    setFailure(null);
    void callOperation('platform.policy.trial_days.set', {
      body: {
        days: Number(form.get('days') ?? ''),
        reason: String(form.get('reason') ?? ''),
      },
    })
      .then(
        () => policyRead.reload(),
        (err: unknown) => setFailure(classifyActionError(err)),
      )
      .finally(() => setSending(false));
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
    >
      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      <ServerRead result={policyRead.result} whatHe="את המדיניות המסחרית" skeletonRows={2}>
        {(data) => (
          <>
            <section className="figures" aria-label="ברירות מחדל מסחריות">
              <article className="figure">
                <p className="figure__label">אורך תקופת ניסיון כברירת מחדל</p>
                <p className="figure__value" dir="ltr">
                  {data.policy.trialDefaultDays}
                </p>
                <p className="figure__note">ימים</p>
              </article>
            </section>

            <section className="breakdown" aria-label="השינוי האחרון">
              <h3 className="breakdown__title">השינוי האחרון</h3>
              <dl className="not-available__list">
                <div className="not-available__item">
                  <dt>מתי</dt>
                  <dd dir="ltr">{formatInstantHe(data.policy.updatedAtUtc)}</dd>
                </div>
                <div className="not-available__item">
                  <dt>מדוע</dt>
                  <dd>{data.policy.reason}</dd>
                </div>
              </dl>
            </section>
          </>
        )}
      </ServerRead>

      {/*
        `signin__form` WAS THE SIGN-IN CARD'S NARROW COLUMN. **Owner section 2, 2026-11-01:**
        *"בהגדרות ניסיון והטבות: שורת בחירה ברורה ושדות צמודים."* The two fields here are a
        number and its reason, and they belong beside each other, which the product's own form
        layout does -- it flows into `repeat(auto-fit, minmax(15rem, 1fr))` and collapses to one
        column under 48rem.

        THE REST OF THAT SENTENCE IS NOT BUILT AND IS NOT PRETENDED. He also asks for independent
        options with an enable mark and alternative options with a single choice, and *"אפשרות
        שלא הופעלה אינה חלה"*. There are no benefit options in this product yet: the only
        commercial default that exists is the trial length, which is this form. A row of switches
        over settings that do not exist would be exactly the simulated capability he forbids.
      */}
      <form className="form" onSubmit={setTrialDays}>
        <div className="form__group form__group--wide">
          <h3 className="form__group-title">שינוי אורך תקופת הניסיון</h3>
          <p className="tile__lead">
            הערך החדש חל על לקוחות שיוקמו מכאן והלאה. תקופת ניסיון של לקוח קיים נקבעת במסך המנוי של
            אותו לקוח ואינה משתנה מכאן.
          </p>
          <label className="field">
            <span className="field__label">מספר ימים</span>
            <input
              className="field__input"
              name="days"
              type="number"
              dir="ltr"
              min={1}
              max={365}
              required
            />
          </label>
          <label className="field">
            {/* REQUIRED, and the database agrees: `CK_commercial_policy_reason`. A commercial
                default that moved with nothing recorded is a change nobody can explain later. */}
            <span className="field__label">סיבת השינוי</span>
            <input className="field__input" name="reason" minLength={3} required />
          </label>
          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={sending}>
              שמירת המדיניות
            </button>
          </div>
        </div>
      </form>
    </AppShell>
  );
}
